import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import {
    Box,
    Button,
    Field,
    Flex,
    Heading,
    Input,
    Link,
    Text,
} from "@chakra-ui/react";
import { useAtomValue, useSetAtom } from "jotai";
import type { FormEvent, PropsWithChildren } from "react";
import { type ReactElement, useState } from "react";
import { LuArrowRight } from "react-icons/lu";
import FullScreenMessage from "./FullScreenMessage";
import { usePromiseToast } from "./hooks/use-promise-toast";
import { sessionAtom } from "./stores/session";
import { getRPC } from "./utils/rpc";

export default function LoginGuard(p: PropsWithChildren<object>) {
    const session = useAtomValue(sessionAtom);

    if (session?.token) return <>{p.children}</>;

    return <LoginForm />;
}

export function LoginForm(): ReactElement {
    const setSession = useSetAtom(sessionAtom);
    const [mode, setMode] = useState<"phoneNumber" | "email">("phoneNumber");
    const [formData, setFormData] = useState({
        phoneNumber: "",
        email: "",
        otp: "",
    });
    const [didSendOTP, setSentOTP] = useState(false);
    const { withToast } = usePromiseToast();

    const handleSubmitT = useAsyncTask(
        async () => {
            withToast(
                async () => {
                    if (didSendOTP) {
                        const res = await getRPC().auth.validateOTP({
                            [mode]: formData[mode],
                            otp: formData.otp,
                        });
                        setSession(res);
                    } else {
                        await getRPC().auth.requestOTP({
                            [mode]: formData[mode],
                        });
                        setSentOTP(true);
                    }
                },
                {
                    messages: true,
                },
            );
        },
        {
            autoRun: false,
            deps: [didSendOTP, formData],
        },
    );

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        handleSubmitT.trigger();
    };

    return (
        <FullScreenMessage>
            <Heading
                size="md"
                textAlign="center"
                fontWeight="semibold"
                color="gray.800"
            >
                Sign in
            </Heading>

            <Box as="form" onSubmit={handleSubmit}>
                {didSendOTP ? (
                    <>
                        <Field.Root mb={4}>
                            <Field.Label fontSize="xs" fontWeight="medium">
                                Enter OTP
                            </Field.Label>
                            <Input
                                type="text"
                                placeholder="Enter 6-digit OTP"
                                value={formData.otp}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        otp: e.target.value,
                                    }))
                                }
                                required
                                pattern="[0-9]{6}"
                                maxLength={6}
                                borderColor="gray.300"
                                fontSize="xs"
                                h="44px"
                            />
                        </Field.Root>

                        <Flex gap={2} direction="column">
                            <Button
                                type="submit"
                                w="full"
                                bg="green.700"
                                color="white"
                                _hover={{ bg: "green.800" }}
                                h="48px"
                                borderRadius="8px"
                                fontWeight="semibold"
                            >
                                <Flex align="center" gap={2}>
                                    <Text>Verify OTP</Text>
                                    <LuArrowRight size={16} />
                                </Flex>
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setSentOTP(false);
                                    setFormData(prev => ({
                                        ...prev,
                                        otp: "",
                                    }));
                                }}
                                disabled={handleSubmitT.runState === "running"}
                            >
                                Back to Phone Number
                            </Button>
                        </Flex>
                    </>
                ) : (
                    <>
                        {mode === "phoneNumber" ? (
                            <Field.Root>
                                <Field.Label fontSize="xs" fontWeight="medium">
                                    Phone number
                                </Field.Label>
                                <Input
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            phoneNumber: e.target.value,
                                        }))
                                    }
                                    maxLength={10}
                                    required
                                    pattern="[0-9]{10}"
                                    borderColor="gray.300"
                                    fontSize="xs"
                                    h="44px"
                                />
                                <Link
                                    fontSize="xs"
                                    onClick={() => {
                                        setMode("email");
                                    }}
                                >
                                    Use Email instead
                                </Link>
                            </Field.Root>
                        ) : (
                            <Field.Root>
                                <Field.Label fontSize="xs" fontWeight="medium">
                                    Email
                                </Field.Label>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            email: e.target.value,
                                        }))
                                    }
                                    required
                                    borderColor="gray.300"
                                    fontSize="xs"
                                    h="44px"
                                />
                                <Link
                                    fontSize="xs"
                                    onClick={() => {
                                        setMode("phoneNumber");
                                    }}
                                >
                                    Use Phone number instead
                                </Link>
                            </Field.Root>
                        )}
                        <Button
                            type="submit"
                            w="full"
                            mt={5}
                            bg="green.700"
                            color="white"
                            _hover={{ bg: "green.800" }}
                            h="48px"
                            borderRadius="8px"
                            disabled={handleSubmitT.runState === "running"}
                            fontWeight="semibold"
                        >
                            <Flex align="center" gap={2}>
                                <Text>Login</Text>
                                <LuArrowRight size={16} />
                            </Flex>
                        </Button>
                    </>
                )}
            </Box>
        </FullScreenMessage>
    );
}
